import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import Swal from 'sweetalert2';
import { toast } from 'react-toastify';
import emptyedu from '../../assets/img/colorkit.svg';
import { getFileURL } from '../../utils/fileURL';
import { deletePatientFile, replaceToolboxFile, uploadPatientFile, getAllPatientFile } from '../../redux/patientSlice';
import Pagination from '../Pagination';
import { Plus, X, Eye, Edit2, FileText, Image, Search, Trash2 } from 'lucide-react';

function FilesTab() {
    const { t } = useTranslation(['filestab']);
    const dispatch = useDispatch();
    const { files, isLoading, selectedPatient, paginationfiles } = useSelector((state) => state.patient);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [modalMode, setModalMode] = useState('upload'); // 'upload' or 'replace'
    const [selectedFile, setSelectedFile] = useState(null);
    const [fileError, setFileError] = useState('');
    const [searchQuery, setSearchQuery] = useState('');
    const [searchError, setSearchError] = useState('');
    const [isUploading, setIsUploading] = useState(false);
    const [selectedFileId, setSelectedFileId] = useState(null);
    const [viewFile, setViewFile] = useState(null);
    const [previewLoading, setPreviewLoading] = useState(true);

    // New fields
    const [filename, setFilename] = useState('');
    const [description, setDescription] = useState('');
    const [filenameError, setFilenameError] = useState('');
    const [descriptionError, setDescriptionError] = useState('');

    const doctorId = selectedPatient?.doctorId;
    const patientId = selectedPatient?.patientId;

    // Pagination state
    const totalItems = paginationfiles?.totalFiles || 0;
    const currentPage = paginationfiles?.page || 1;
    const itemsPerPage = paginationfiles?.limit || 5;
    const totalPages = paginationfiles?.totalPages || 1;

    // Validation: No consecutive special chars
    const hasConsecutiveSpecialChars = (str) => {
        return /[^a-zA-Z0-9\s\-_.]{2,}/.test(str);
    };

    // Validate search input
    const validateSearchInput = (value) => {
        // Sanitize input - remove dangerous characters
        const sanitized = value.replace(/[<>]/g, '');

        // Check length
        if (sanitized.length > 50) {
            return t('patientFilesTab.search_exceed_50');
        }

        // Check for only whitespace
        if (sanitized.trim().length === 0 && sanitized.length > 0) {
            return t('patientFilesTab.search_only_whitespace');
        }

        return '';
    };

    const validateFilename = (name) => {
        if (!name || name.trim() === '') return t('filestab.error_filename_required');
        if (/^\s+$/.test(name)) return t('patientFilesTab.filename_only_whitespace');
        if (name.trim().length < 2) return t('patientFilesTab.filename_min_2');
        if (name.length > 20) return t('filestab.error_filename_max');
        if (hasConsecutiveSpecialChars(name)) return t('filestab.error_filename_special_chars');
        return '';
    };

    const validateDescription = (desc) => {
        if (desc.length > 50) return t('filestab.error_description_max');
        if (/^\s+$/.test(desc) && desc.length > 0) return t('patientFilesTab.description_only_whitespace');
        return '';
    };

    const validateFile = (file) => {
        if (!file) return t('filestab.error_no_file');

        const maxSize = 5 * 1024 * 1024; // 5MB
        const validTypes = ['image/jpeg', 'application/pdf'];

        if (!validTypes.includes(file.type)) {
            return t('filestab.error_invalid_file');
        }

        if (file.size > maxSize) {
            return t('filestab.error_file_size');
        }

        return '';
    };

    const resetModalFields = () => {
        setSelectedFile(null);
        setFileError('');
        setFilename('');
        setDescription('');
        setFilenameError('');
        setDescriptionError('');
        setSelectedFileId(null);
        setModalMode('upload');
    };

    const handleAddClick = () => {
        setModalMode('upload');
        setIsModalOpen(true);
        resetModalFields();
    };

    const handleReplaceClick = (fileId, currentFile) => {
        setModalMode('replace');
        setSelectedFileId(fileId);
        setFilename(currentFile.filename.split('.').slice(0, -1).join('.') || '');
        setDescription(currentFile.description || '');
        setIsModalOpen(true);
        setSelectedFile(null);
        setFileError('');
    };

    const handleCloseModal = () => {
        setIsModalOpen(false);
        resetModalFields();
    };

    const handleFileChange = (event) => {
        const file = event.target.files[0];
        const error = validateFile(file);

        if (error) {
            setFileError(error);
            setSelectedFile(null);
        } else {
            setFileError('');
            setSelectedFile(file);
            // Auto-fill filename if empty
            if (!filename) {
                const nameWithoutExt = file.name.split('.').slice(0, -1).join('.');
                setFilename(nameWithoutExt.slice(0, 20));
            }
        }
    };

    // Handle search input change
    const handleSearchChange = (e) => {
        const value = e.target.value;

        // Sanitize input - remove dangerous characters
        const sanitized = value.replace(/[<>]/g, '');
        const finalValue = sanitized.length > 50
            ? sanitized.substring(0, 50)
            : sanitized;

        // Always update searchQuery so the input stays in sync
        setSearchQuery(finalValue);

        // Validate input
        const error = validateSearchInput(finalValue);
        setSearchError(error);
    };

    const handleFilenameChange = (e) => {
        const value = e.target.value;

        // Sanitize input - remove dangerous characters
        const sanitized = value.replace(/[<>]/g, '');
        // Enforce max length
        const finalValue = sanitized.length > 20 ? sanitized.substring(0, 20) : sanitized;

        setFilename(finalValue);
        const error = validateFilename(finalValue);
        setFilenameError(error);
    };

    const handleDescriptionChange = (e) => {
        const value = e.target.value;

        // Sanitize input - remove dangerous characters
        const sanitized = value.replace(/[<>]/g, '');
        // Enforce max length
        const finalValue = sanitized.length > 50 ? sanitized.substring(0, 50) : sanitized;

        setDescription(finalValue);
        const error = validateDescription(finalValue);
        setDescriptionError(error);
    };

    const handleSubmit = async () => {
        const fileErr = validateFile(selectedFile);
        const nameErr = validateFilename(filename);
        const descErr = validateDescription(description);

        if (fileErr || nameErr) {
            setFileError(fileErr);
            setFilenameError(nameErr);
            setDescriptionError(descErr);
            return;
        }

        const result = await Swal.fire({
            title: modalMode === 'replace'
                ? t('filestab.replace_confirm_title')
                : t('filestab.upload_confirm_title'),
            text: modalMode === 'replace'
                ? t('filestab.replace_confirm_text')
                : t('filestab.upload_confirm_text'),
            icon: "warning",
            showCancelButton: true,
            confirmButtonText: modalMode === 'replace'
                ? t('filestab.replace_confirm_yes')
                : t('filestab.upload_confirm_yes'),
            cancelButtonText: t('filestab.cancel'),
            confirmButtonColor: "#4ABDBD",
            cancelButtonColor: "#d33",
        });

        if (!result.isConfirmed) return;

        setIsUploading(true);
        const formData = new FormData();
        formData.append(modalMode === 'replace' ? 'file' : 'files', selectedFile);
        formData.append('filename', filename.trim());
        formData.append('description', description.trim());

        const obj = { docId: 'null', patientId: patientId, date: 'null' };

        try {
            if (modalMode === 'replace') {
                await dispatch(
                    replaceToolboxFile({
                        fileData: formData,
                        fileId: selectedFileId,
                        obj,
                    })
                ).unwrap();
            } else {
                await dispatch(
                    uploadPatientFile({
                        fileData: formData,
                        obj,
                    })
                ).unwrap();
            }

            handleCloseModal();

            toast.success(modalMode === 'replace'
                    ? t('filestab.replace_success_text')
                    : t('filestab.upload_success_text'));
        } catch (error) {
            console.error('Error during file upload/replace:', error);
            toast.warn(error?.error || t('filestab.error_general'));
        } finally {
            setIsUploading(false);
        }
    };

    const handleViewFile = (file) => {
        setPreviewLoading(true);
        setViewFile(file);
    };

    const handleCloseViewModal = () => {
        setViewFile(null);
    };

    const handleDeleteFile = async (fileId, filename) => {
        const result = await Swal.fire({
            title: t('filestab.delete_confirm_title'),
            text: t('filestab.confirm_delete', { filename }),
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: '#4ABDBD',
            cancelButtonColor: "#99a1af",
            confirmButtonText: t('filestab.delete_confirm'),
            cancelButtonText: t('filestab.cancel'),
        });

        if (!result.isConfirmed) return;

        try {
            await dispatch(deletePatientFile({ obj: { docId: 'null', patientId: patientId, date: 'null' }, fileId })).unwrap();

            toast.success(t('filestab.delete_success_text', { filename }));
        } catch (error) {
            toast.warn(error?.error || t('filestab.error_general'));
        }
    };

    const query = searchQuery?.toLowerCase() || '';
    const filteredFiles = files?.length
        ? files.filter((file) => file?.filename?.toLowerCase().includes(query))
        : [];

    // Remove useEffect for fetching files
    // Only call getAllPatientFile in handlePageChange and after patient selection
    // Initial fetch when patient is selected (call in handler, not useEffect)
    // Example: if you have a patient selection handler, call getAllPatientFile there
    // Remove this block:
    // React.useEffect(() => {
    //     if (selectedPatient?.doctorId && selectedPatient?.patientId) {
    //         dispatch(getAllPatientFile({
    //             docId: selectedPatient.doctorId,
    //             patientId: selectedPatient.patientId,
    //             date: 'null',
    //             page: 1,
    //             limit: itemsPerPage,
    //         }));
    //     }
    // }, [selectedPatient?.doctorId, selectedPatient?.patientId, dispatch]);

    // Pagination handler (fetches files for new page/limit)
    const handlePageChange = (page, newItemsPerPage) => {
        const limit = newItemsPerPage || itemsPerPage;
        dispatch(getAllPatientFile({
            docId: 'null',
            patientId: selectedPatient?.patientId,
            date: 'null',
            page,
            limit,
        }));
    };

    return (
        <div className="mt-4" data-testid="files-tab-container">
            {/* Header */}
            <div className="flex flex-col sm:flex-row flex-wrap gap-4 justify-between items-center mb-6" data-testid="files-tab-header">
                <h2 className="text-lg sm:text-xl md:text-2xl font-bold bg-linear-to-r from-[#4ABDBD] to-[#3AA8A8] bg-clip-text text-transparent" data-testid="files-tab-title">
                    {t('filestab.heading')}
                </h2>
                <div className="flex flex-col sm:flex-row gap-3 w-full sm:w-auto" data-testid="files-tab-controls">
                    <div className="relative w-full sm:w-56 md:w-64" data-testid="files-tab-search-container">
                        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" data-testid="files-tab-search-icon" />
                        <input
                            id="patient-profile-files-search-input"
                            type="text"
                            placeholder={t('filestab.search_placeholder')}
                            value={searchQuery}
                            onChange={handleSearchChange}
                            maxLength={50}
                            className={`pl-10 px-4 py-2 w-full rounded-xl border focus:outline-none focus:ring-2 focus:ring-[#4ABDBD] hover:border-[#4ABDBD] text-sm transition-all duration-300 ${searchError
                                    ? 'border-red-300 bg-red-50'
                                    : 'border-gray-300'
                                }`}
                            data-testid="files-tab-search-input"
                        />
                        {searchError && (
                            <p className="mt-1 text-xs text-red-500" data-testid="files-tab-search-error">{searchError}</p>
                        )}
                    </div>
                    <button
                        id="patient-profile-files-add-button"
                        onClick={handleAddClick}
                        // disabled={!doctorId}
                        className={`group flex items-center gap-2 px-4 py-2 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 font-semibold text-sm w-full sm:w-auto justify-center
                            bg-linear-to-r from-[#4ABDBD] via-[#3AA8A8] to-[#2D8A8A] text-white hover:scale-105 cursor-pointer`}
                        data-testid="files-tab-add-button"
                    >
                        <Plus className="w-5 h-5" data-testid="files-tab-add-icon" />
                        <span>{t('filestab.add_file')}</span>
                    </button>
                </div>
            </div>

            {isLoading && (
                <p className="text-center text-gray-600 text-sm" data-testid="files-tab-loading-text">{t('filestab.loading')}</p>
            )}

            {!isLoading && filteredFiles.length === 0 && (
                <div className="flex flex-col items-center justify-center col-span-full min-h-[200px] px-4 sm:px-6 lg:px-8" data-testid="files-tab-empty-state">
                    <img
                        src={emptyedu}
                        alt={t('filestab.no_files_found')}
                        className="w-24 h-24 sm:w-32 sm:h-32 md:w-40 md:h-40 lg:w-56 lg:h-56 object-contain"
                        data-testid="files-tab-empty-image"
                    />
                    <p className="text-gray-500 text-center text-sm sm:text-base md:text-lg" data-testid="files-tab-empty-text">
                        {t('filestab.no_files')}
                    </p>
                </div>
            )}

            {!isLoading && filteredFiles.length > 0 && (
                <>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-3 gap-3 sm:gap-4" data-testid="files-tab-grid">
                        {filteredFiles.map((file) => {
                            const FileIcon = file.fileType === 'pdf' ? FileText : Image;
                            return (
                                <div
                                    key={file._id}
                                    className="group relative flex items-center gap-4 rounded-xl border border-gray-200 bg-linear-to-r from-gray-50 to-blue-50/30 shadow-md hover:shadow-lg hover:shadow-[#4ABDBD]/20 transition-all duration-300 p-4 hover:scale-105"
                                    data-testid={`files-tab-card-${file._id}`}
                                >
                                    <div className="shrink-0 bg-linear-to-r from-[#4ABDBD]/20 to-[#3AA8A8]/20 p-3 rounded-xl shadow-sm" data-testid={`files-tab-card-icon-container-${file._id}`}>
                                        <FileIcon className="w-8 h-8 sm:w-10 sm:h-10 text-[#4ABDBD]" data-testid={`files-tab-card-icon-${file._id}`} />
                                    </div>

                                    <div className="flex-1 min-w-0" data-testid={`files-tab-card-info-${file._id}`}>
                                        <h3 className="text-sm font-bold text-gray-700 group-hover:text-[#4ABDBD] transition-colors truncate" data-testid={`files-tab-card-filename-${file._id}`}>
                                            {file.filename.length > 13
                                                ? `${file.filename.slice(0, 13)}...`
                                                : file.filename}
                                        </h3>
                                        <p className="text-xs text-gray-600 mt-1" data-testid={`files-tab-card-created-at-${file._id}`}>
                                            {t('filestab.added_at')} {new Date(file.createdAt).toLocaleDateString()}
                                        </p>
                                        {file.description && (
                                            <p className="text-xs text-gray-500 mt-1 truncate" data-testid={`files-tab-card-description-${file._id}`}>
                                                {file.description}
                                            </p>
                                        )}
                                    </div>

                                    <div className="flex gap-2 ml-auto opacity-70 group-hover:opacity-100 transition-opacity" data-testid={`files-tab-card-actions-${file._id}`}>
                                        <button
                                            id={`patient-profile-files-view-button-${file._id}`}
                                            onClick={() => handleViewFile(file)}
                                            className="group/btn text-[#4ABDBD] hover:text-white p-2 rounded-xl bg-gradient-to-r from-blue-50 to-cyan-50 hover:from-[#4ABDBD] hover:to-[#3AA8A8] shadow-md hover:shadow-lg cursor-pointer transition-all duration-300 hover:scale-110"
                                            title={t('filestab.view_file_title')}
                                            data-testid={`files-tab-view-button-${file._id}`}
                                        >
                                            <Eye className="w-4 h-4" data-testid={`files-tab-view-icon-${file._id}`} />
                                        </button>
                                        <button
                                            id={`patient-profile-files-replace-button-${file._id}`}
                                            onClick={() => handleReplaceClick(file.fileId, file)}
                                            className="group/btn text-[#4ABDBD] hover:text-white p-2 rounded-xl bg-gradient-to-r from-blue-50 to-cyan-50 hover:from-[#4ABDBD] hover:to-[#3AA8A8] shadow-md hover:shadow-lg cursor-pointer transition-all duration-300 hover:scale-110"
                                            title={t('filestab.replace_file_title')}
                                            data-testid={`files-tab-replace-button-${file._id}`}
                                        >
                                            <Edit2 className="w-4 h-4" data-testid={`files-tab-replace-icon-${file._id}`} />
                                        </button>
                                        <button
                                            id={`patient-profile-files-delete-button-${file._id}`}
                                            onClick={() => handleDeleteFile(file.fileId, file.filename)}
                                            className="group/btn text-red-500 hover:text-white p-2 rounded-xl bg-gradient-to-r from-red-50 to-pink-50 hover:from-red-500 hover:to-pink-500 shadow-md hover:shadow-lg cursor-pointer transition-all duration-300 hover:scale-110"
                                            title={t('filestab.delete_file_title')}
                                            data-testid={`files-tab-delete-button-${file._id}`}
                                        >
                                            <Trash2 className="w-4 h-4" data-testid={`files-tab-delete-icon-${file._id}`} />
                                        </button>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                    {/* Pagination (server-side) */}
                    {totalItems > 0 && (
                        <div className="mt-4">
                            <Pagination
                                totalItems={totalItems}
                                currentPage={currentPage}
                                itemsPerPage={itemsPerPage}
                                onPageChange={handlePageChange}
                            />
                        </div>
                    )}
                </>
            )}

            {/* Upload / Replace Modal */}
            {isModalOpen && (
                <div
                    className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
                    role="dialog"
                    aria-labelledby="patient-profile-files-modal-title"
                    data-testid="files-tab-modal"
                >
                    <div className="bg-white my-5 rounded-2xl shadow-2xl w-full max-w-md sm:max-w-lg md:max-w-2xl max-h-[80vh] sm:max-h-[90vh] flex flex-col" data-testid="files-tab-modal-content">
                        <div className="p-4 sm:p-6 sticky top-0 bg-linear-to-r from-[#4ABDBD]/5 to-[#3AA8A8]/5 z-10 flex justify-between items-center border-b border-gray-200" data-testid="files-tab-modal-header">
                            <h3
                                className="text-base sm:text-lg md:text-xl font-bold bg-linear-to-r from-[#4ABDBD] to-[#3AA8A8] bg-clip-text text-transparent"
                                id="patient-profile-files-modal-title"
                                data-testid="files-tab-modal-title"
                            >
                                {modalMode === 'replace' ? t('filestab.replace_file_heading') : t('filestab.upload_file_heading')}
                            </h3>
                            <button
                                onClick={handleCloseModal}
                                aria-label={t('filestab.close_modal')}
                                className="group/btn text-gray-500 hover:text-white p-2 rounded-xl bg-gradient-to-r from-gray-100 to-gray-200 hover:from-gray-400 hover:to-gray-500 shadow-md hover:shadow-lg cursor-pointer transition-all duration-300 hover:scale-110"
                                data-testid="files-tab-modal-close-button"
                            >
                                <X className="w-5 h-5 sm:w-6 sm:h-6" data-testid="files-tab-modal-close-icon" />
                            </button>
                        </div>

                        <div className="flex-1 overflow-y-auto px-4 sm:px-6 py-4 custom-scrollbar space-y-4" data-testid="files-tab-modal-body">
                            {/* File Input */}
                            <div data-testid="files-tab-file-field-container">
                                <label className="block text-sm font-semibold text-gray-700 mb-2" data-testid="files-tab-file-label">
                                    {t('filestab.select_file_label')}
                                </label>
                                <div className="relative">
                                    <input
                                        id="patient-profile-files-upload-input"
                                        type="file"
                                        accept="image/jpeg,application/pdf"
                                        onChange={handleFileChange}
                                        className="hidden"
                                        data-testid="files-tab-file-input"
                                    />
                                    <label
                                        htmlFor="patient-profile-files-upload-input"
                                        className="flex items-center gap-4 w-full p-3 rounded-xl border border-gray-300 hover:border-[#4ABDBD] focus-within:ring-2 focus-within:ring-[#4ABDBD] transition-all duration-300 cursor-pointer"
                                        data-testid="files-tab-file-input-trigger"
                                    >
                                        <span className="px-4 py-2 rounded-xl bg-linear-to-r from-[#4ABDBD] to-[#3AA8A8] text-white text-sm font-semibold" data-testid="files-tab-choose-file-text">
                                            {t('filestab.choose_file')}
                                        </span>
                                        <span className="text-sm text-gray-500 truncate" data-testid="files-tab-selected-file-text">
                                            {selectedFile ? selectedFile.name : t('filestab.no_file_chosen')}
                                        </span>
                                    </label>
                                </div>
                                {fileError && (
                                    <p className="mt-2 text-xs text-red-500 font-medium" data-testid="files-tab-file-error">{fileError}</p>
                                )}
                            </div>

                            {/* Filename Input */}
                            <div data-testid="files-tab-filename-field-container">
                                <label className="block text-sm font-semibold text-gray-700 mb-2" data-testid="files-tab-filename-label">
                                    <span className="text-red-500">*</span> {t('filestab.filename_label')}
                                    <span className="text-xs text-gray-500 ml-1" data-testid="files-tab-filename-counter">({filename.length}/20)</span>
                                </label>
                                <input
                                    type="text"
                                    value={filename}
                                    onChange={handleFilenameChange}
                                    maxLength={20}
                                    placeholder={t('filestab.filename_placeholder')}
                                    className={`w-full px-4 py-2 rounded-xl border focus:outline-none focus:ring-2 focus:ring-[#4ABDBD] text-sm transition-all duration-300 ${filenameError
                                            ? 'border-red-500 bg-red-50'
                                            : 'border-gray-300'
                                        }`}
                                    data-testid="files-tab-filename-input"
                                />
                                {filenameError && (
                                    <p className="mt-1 text-xs text-red-500 font-medium" data-testid="files-tab-filename-error">{filenameError}</p>
                                )}
                            </div>

                            {/* Description Input */}
                            <div data-testid="files-tab-description-field-container">
                                <label className="block text-sm font-semibold text-gray-700 mb-2" data-testid="files-tab-description-label">
                                    {t('filestab.description_label')}
                                    <span className={`text-xs ml-1 ${description.length > 50 ? 'text-red-500' : 'text-gray-500'}`} data-testid="files-tab-description-counter">
                                        ({description.length}/50)
                                    </span>
                                </label>
                                <textarea
                                    value={description}
                                    onChange={handleDescriptionChange}
                                    maxLength={50}
                                    rows={3}
                                    placeholder={t('filestab.description_placeholder')}
                                    className={`w-full px-4 py-2 rounded-xl border focus:outline-none focus:ring-2 focus:ring-[#4ABDBD] text-sm transition-all duration-300 resize-none ${descriptionError
                                            ? 'border-red-500 bg-red-50'
                                            : 'border-gray-300'
                                        }`}
                                    data-testid="files-tab-description-input"
                                />
                                {descriptionError && (
                                    <p className="mt-1 text-xs text-red-500 font-medium" data-testid="files-tab-description-error">{descriptionError}</p>
                                )}
                            </div>
                        </div>

                        <div className="sticky bottom-0 p-4 sm:p-6 border-t rounded-2xl border-gray-200 bg-linear-to-r from-gray-50/50 to-blue-50/30 flex justify-end gap-4" data-testid="files-tab-modal-footer">
                            <button
                                type="button"
                                onClick={handleCloseModal}
                                className="px-4 py-2 bg-gradient-to-r from-gray-200 to-gray-300 hover:from-gray-300 hover:to-gray-400 text-gray-700 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 font-semibold text-sm hover:scale-105 cursor-pointer"
                                disabled={isUploading}
                                data-testid="files-tab-modal-cancel-button"
                            >
                                {t('filestab.cancel')}
                            </button>
                            <button
                                type="button"
                                onClick={handleSubmit}
                                disabled={!selectedFile || isUploading || !!filenameError || !filename.trim()}
                                className={`group flex items-center justify-center gap-2 px-4 py-2 bg-gradient-to-r from-[#4ABDBD] via-[#3AA8A8] to-[#2D8A8A] text-white rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 font-semibold text-sm ${selectedFile && !isUploading && !filenameError && filename.trim()
                                        ? 'hover:scale-105 cursor-pointer'
                                        : 'opacity-50 cursor-not-allowed'
                                    }`}
                                data-testid="files-tab-modal-submit-button"
                            >
                                {isUploading && (
                                    <span className="border-2 border-white border-t-transparent rounded-full w-4 h-4 animate-spin" data-testid="files-tab-modal-submit-loading"></span>
                                )}
                                {isUploading
                                    ? t('filestab.uploading')
                                    : modalMode === 'replace'
                                        ? t('filestab.replace')
                                        : t('filestab.upload')}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* View Modal */}
            {viewFile && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="files-tab-view-modal">
                    <div className="bg-white my-5 rounded-2xl shadow-2xl w-full max-w-4xl max-h-[80vh] sm:max-h-[90vh] flex flex-col" data-testid="files-tab-view-modal-content">
                        <div className="p-4 sm:p-6 sticky top-0 bg-linear-to-r from-[#4ABDBD]/5 to-[#3AA8A8]/5 z-10 flex justify-between items-center border-b border-gray-200" data-testid="files-tab-view-modal-header">
                            <h3
                                className="text-base sm:text-lg md:text-xl font-bold bg-linear-to-r from-[#4ABDBD] to-[#3AA8A8] bg-clip-text text-transparent truncate max-w-[70%]"
                                title={viewFile.filename}
                                data-testid="files-tab-view-modal-title"
                            >
                                {viewFile.filename.length > 30
                                    ? `${viewFile.filename.slice(0, 30)}...`
                                    : viewFile.filename}
                            </h3>
                            <button
                                onClick={handleCloseViewModal}
                                className="group/btn text-gray-500 hover:text-white p-2 rounded-xl bg-gradient-to-r from-gray-100 to-gray-200 hover:from-gray-400 hover:to-gray-500 shadow-md hover:shadow-lg cursor-pointer transition-all duration-300 hover:scale-110"
                                data-testid="files-tab-view-modal-close-button"
                            >
                                <X className="w-5 h-5 sm:w-6 sm:h-6" data-testid="files-tab-view-modal-close-icon" />
                            </button>
                        </div>

                        <div className="flex-1 overflow-y-auto px-4 sm:px-6 py-4 custom-scrollbar" data-testid="files-tab-view-modal-body">
                            {viewFile.fileType === 'pdf' ? (
                                <div className="relative w-full pb-[75%] sm:pb-[60%] md:pb-[56.25%] lg:pb-[50%] xl:pb-[45%]" data-testid="files-tab-view-pdf-container">
                                    <iframe
                                        src={getFileURL(viewFile.filePath)}
                                        className="absolute top-0 left-0 w-full h-full border-0 rounded-lg shadow"
                                        title={viewFile.filename}
                                        onLoad={() => setPreviewLoading(false)}
                                        onError={() => setPreviewLoading(false)}
                                        data-testid="files-tab-view-pdf-iframe"
                                    />
                                    {previewLoading && (
                                        <div className="absolute inset-0 flex items-center justify-center bg-white/80 rounded-lg" data-testid="files-tab-view-pdf-loading">
                                            <span className="border-4 border-[#4ABDBD] border-t-transparent rounded-full w-10 h-10 animate-spin"></span>
                                        </div>
                                    )}
                                </div>
                            ) : (
                                <div className="relative" data-testid="files-tab-view-image-container">
                                    <img
                                        src={getFileURL(viewFile.filePath)}
                                        alt={viewFile.filename}
                                        className="w-full h-auto max-h-full object-contain"
                                        onLoad={() => setPreviewLoading(false)}
                                        onError={() => setPreviewLoading(false)}
                                        data-testid="files-tab-view-image"
                                    />
                                    {previewLoading && (
                                        <div className="absolute inset-0 flex items-center justify-center bg-white/80 rounded-lg" data-testid="files-tab-view-image-loading">
                                            <span className="border-4 border-[#4ABDBD] border-t-transparent rounded-full w-10 h-10 animate-spin"></span>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>

                        <div className="sticky bottom-0 p-4 sm:p-6 border-t rounded-2xl border-gray-200 bg-linear-to-r from-gray-50/50 to-blue-50/30 flex justify-end gap-4" data-testid="files-tab-view-modal-footer">
                            <a
                                href={getFileURL(viewFile.filePath)}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="group flex items-center justify-center gap-2 px-4 py-2 bg-gradient-to-r from-[#4ABDBD] via-[#3AA8A8] to-[#2D8A8A] text-white rounded-xl shadow-lg hover:shadow-xl hover:scale-105 transition-all duration-300 font-semibold text-sm"
                                data-testid="files-tab-view-download-link"
                            >
                                <span>{t('filestab.download')}</span>
                            </a>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default FilesTab;